import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { CapabilityClaim, RouteOption } from "../../../api/threads/routeOptions";
import { formatDate } from "../../../shared/formatting/dateTimes";
import { appPaths } from "../../../shared/routing/appPaths";
import { FORM_HELP_TEXT_CLASS } from "../../../ui/styles/formStyles";
import { prefillQuery } from "../../processAuthoring/capabilities/overrideDrafts";
import { assertableGaps } from "./capabilityFacts";
import { hintText } from "./routeFitText";
import EmptyState from "../../../ui/feedback/EmptyState";

/**
 * What DMW knows about the chosen model, and where each fact came from (capabilities.md
 * CAP-3): the agent or provider, a probe, finished turns, or an override. For a capability
 * nobody has stated, which the turns run without (D17), it offers a reviewed override for
 * the project, and for API routes the probe the host can run on their PC.
 */
export default function RouteCapabilityDetails({ option }: { option: RouteOption | undefined }) {
  const { t } = useTranslation();
  if (option === undefined || !option.available) {
    return null;
  }
  const gaps = assertableGaps(option);
  return (
    <details className="text-sm" open={gaps.length > 0}>
      <summary className="app-label cursor-pointer">{t("threads.hosting.facts.title", { count: option.capabilities.length })}</summary>
      {option.capabilities.length === 0 ? (
        <EmptyState size="small" className="mt-1">{t("threads.hosting.facts.none")}</EmptyState>
      ) : (
        <table className="mt-2 w-full text-xs">
          <thead>
            <tr className="app-text-muted text-left">
              <th className="pr-2 font-normal">{t("threads.hosting.facts.capability")}</th>
              <th className="pr-2 font-normal">{t("threads.hosting.facts.value")}</th>
              <th className="font-normal">{t("threads.hosting.facts.source")}</th>
            </tr>
          </thead>
          <tbody>
            {option.capabilities.map((capability) => (
              <tr key={capability.key} className="align-top">
                <td className="pr-2 font-mono" title={capability.description ?? t("threads.hosting.facts.outsideVocabulary")}>{capability.key}</td>
                <td className="pr-2">
                  {capability.decided !== null
                    ? valueText(t, capability.decided.value)
                    : capability.hint !== null
                      ? hintText(t, capability.hint.value)
                      : t("threads.hosting.facts.conflict")}
                </td>
                <td>
                  {capability.decided === null ? null : <ClaimSource claim={capability.decided} />}
                  {capability.decided === null && capability.hint !== null ? <ClaimSource claim={capability.hint} /> : null}
                  {capability.disagreeing.map((claim) => (
                    <p key={`${claim.source}-${claim.evidence}`} className="app-text-muted">
                      {t("threads.hosting.facts.disagrees", { value: valueText(t, claim.value) })} <ClaimSource claim={claim} />
                    </p>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {gaps.length === 0 ? null : (
        <div className="mt-2 space-y-1">
          <p className={FORM_HELP_TEXT_CLASS}>{t("threads.hosting.facts.assertHint")}</p>
          <ul className="list-disc space-y-0.5 pl-4 text-xs">
            {gaps.map((gap) => (
              <li key={gap.key}>
                <Link to={appPaths.newCapabilityOverrides(prefillQuery(gap))} className="app-link app-link-cue-away underline">
                  {t("threads.hosting.facts.assert", { capability: gap.key })}
                </Link>
              </li>
            ))}
          </ul>
          {option.route.kind === "api_key" ? (
            <p className={FORM_HELP_TEXT_CLASS}>
              {t("threads.hosting.facts.probeHint")}{" "}
              <code className="app-code-line px-1 font-mono">dmw-connector capabilities probe {option.route.route_id} {option.route.model_id}</code>
            </p>
          ) : null}
        </div>
      )}
    </details>
  );
}

function ClaimSource({ claim }: { claim: CapabilityClaim }) {
  const { t, i18n } = useTranslation();
  const date = claim.observedAt === null ? "" : ` · ${formatDate(claim.observedAt, i18n.resolvedLanguage)}`;
  return (
    <span>
      <span className="font-semibold">{t(`threads.hosting.facts.sources.${claim.source}`)}</span>
      <span className="app-text-muted"> · {claim.evidence}{date}</span>
      {claim.override === null ? null : (
        <>
          {" · "}
          <Link to={appPaths.item(claim.override)} className="app-link app-link-cue-away underline">{t("threads.hosting.facts.openOverride")}</Link>
        </>
      )}
    </span>
  );
}

function valueText(t: (key: string) => string, value: boolean | number | string): string {
  if (typeof value === "boolean") {
    return t(value ? "threads.hosting.facts.yes" : "threads.hosting.facts.no");
  }
  return typeof value === "number" ? value.toLocaleString() : value;
}
